import styled from 'styled-components'
import { chartColor } from 'components/ui/chart'
import type { ChartTone } from 'models/chart'
import { stoColor, stoMedia, stoRadius, stoSpace, stoType } from 'constants/stoTheme'

/**
 * Figma: round token icon, 28px or 32px. Project logos are often wide wordmarks, so the whole
 * image is scaled down inside the circle rather than cropped. The hero chip shows the logo bare: it carries its own grey ring.
 */
export const TickerIcon = styled.img<{ $size: 28 | 32; $bare?: boolean }>`
  flex: 0 0 auto;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  padding: ${({ $bare }) => ($bare ? 0 : 3)}px;
  border-radius: ${stoRadius.full};
  background: ${({ $bare }) => ($bare ? 'transparent' : stoColor.surface)};
  object-fit: contain;
`

export const Identity = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: ${stoSpace[2]};
  min-width: 0;
`

export const IdentityText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

/** Figma 3356:77170: "BMN2 -" at 2px tracking, the label at 0.4px. */
export const SymbolLine = styled.p`
  ${stoType.bodySm};
  margin: 0;
  letter-spacing: 0.4px;
  overflow: hidden;
  color: ${stoColor.textSecondary};
  text-overflow: ellipsis;
  white-space: nowrap;

  strong {
    font-weight: 400;
    letter-spacing: 2px;
    text-transform: uppercase;
  }
`

export const ValueLine = styled.p`
  display: flex;
  align-items: baseline;
  gap: ${stoSpace[2]};
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.8px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

export const Value = styled.span`
  font-weight: 700;
  color: ${stoColor.textPrimary};
`

export const Change = styled.span<{ $tone: ChartTone }>`
  font-weight: 600;
  color: ${({ $tone }) =>
    $tone === 'up' ? chartColor.positive : $tone === 'down' ? chartColor.negative : stoColor.textSecondary};
`

/**
 * The change's range, after it and smaller: "+13.74% YTD". Its own line height: the row's, at this
 * size on the shared baseline, grew the row by 1px and shifted the text when it came and went.
 */
export const RangeTag = styled.span`
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: 0.6px;
  color: ${stoColor.textTertiary};
`

/** Figma "Summary footer": "As of … - Source: …" under a chart card. */
export const Footnote = styled.p`
  margin: 0;
  font-size: 11px;
  line-height: 16px;
  letter-spacing: 0.5px;
  color: ${stoColor.textTertiary};

  ${stoMedia.belowDesktop} {
    font-weight: 400;
  }
`

/** Plot height of the full chart cards (Figma). */
export const CHART_HEIGHT = 228
/** With value and date axes the plot needs room for the labels (Performance charts). */
export const AXIS_CHART_HEIGHT = 300

/** A note on a chart's data under its date and source (BMN2: its mining dashboard), in the disclaimer style. */
export const ChartNote = styled.p`
  ${stoType.disclaimer};
  margin: 0;
  color: ${stoColor.neutral500};
`

export const ChartNoteLink = styled.a`
  color: inherit !important;
  text-decoration: underline;
`
